import React, { useEffect } from 'react'
import { Col, message, Row, Tooltip } from 'antd'
import isEmpty from 'lodash/isEmpty'
import Loading from 'components/Utils/Loading'
import { netCommissionRate } from './utils'
import './styles.css'
import { QuestionCircleOutlined } from '@ant-design/icons'
import styled from '@emotion/styled'
import {
  useGetMerchantCommissionRate,
  useGetZeroOrderCommissionRate,
  useGetTransactionFee
} from 'hooks/CommissionRates'

interface CommissionRatesProps {
  merchantCurrentPlanSlug: string
  zeroOrderRateId: string
  transactionPlanId: string | null
}

const VAT_PERCENTAGE = '1.2'

const CommissionRates = ({
  merchantCurrentPlanSlug,
  zeroOrderRateId,
  transactionPlanId
}: CommissionRatesProps) => {
  const [
    commission_rates,
    { loading: loadingRate, error: getMerchantCommissionRateError }
  ] = useGetMerchantCommissionRate({
    merchantCurrentPlanSlug
  })
  const [
    zero_commission_rates,
    { loading: loadingZeroRate, error: getZeroOrderCommissionRateError }
  ] = useGetZeroOrderCommissionRate({
    zeroOrderRateId
  })
  const [
    transactionFee,
    { loading: loadingTransactionFee, error: getTransactionFeeError }
  ] = useGetTransactionFee({
    transactionPlanId
  })

  useEffect(() => {
    if (!isEmpty(getMerchantCommissionRateError)) {
      message.error(
        `Unable to fetch commission rates due to: ${getMerchantCommissionRateError?.message}`,
        5
      )
    }

    if (!isEmpty(getZeroOrderCommissionRateError)) {
      message.error(
        `Unable to fetch zero order commission rates due to: ${getZeroOrderCommissionRateError?.message}`,
        5
      )
    }

    if (!isEmpty(getTransactionFeeError)) {
      message.error(
        `Unable to fetch transaction fees due to: ${getTransactionFeeError?.message}`,
        5
      )
    }
  }, [
    getMerchantCommissionRateError,
    getZeroOrderCommissionRateError,
    getTransactionFeeError
  ])

  if (loadingRate || loadingZeroRate || loadingTransactionFee)
    return <Loading />

  const getTableRow = (values: (string | React.ReactNode | null)[]) => {
    const colSpan = 24 / values.length
    return (
      <Row className='row' align='middle'>
        {values.map((value, index) => {
          return (
            <Col
              key={`${index}-${value}`}
              className='_text-center'
              span={colSpan}
            >
              {value}
            </Col>
          )
        })}
      </Row>
    )
  }

  const getComissionRow = (values: (string | null)[]) => {
    const commissionValues = values.map((value) =>
      value !== null ? netCommissionRate(value, VAT_PERCENTAGE) + '%' : '-'
    )

    return getTableRow(commissionValues)
  }

  const moneyToFixed = (value: string) => {
    return `£${parseFloat(value).toFixed(2)}`
  }

  const percentageToFixed = (value: string) => {
    return `${(parseFloat(value) * 100).toFixed(1)}%`
  }

  const zeroOrderTooltipMessage =
    'Commission will apply to purchases where products are discounted to £0 on checkout. This does not apply to rewards gained through loyalty.'

  return (
    <Root>
      <Row>
        <Col span={12}>
          <Title>
            <span>Commission Rates</span>
          </Title>
        </Col>
      </Row>

      <Row data-testid='loyaltyStats' justify='center' align='middle'>
        <Col span={3}>
          <PricingHeader>
            <div className='title'>
              <span>Same Day</span>
            </div>
            <div className='subtitle'>
              <span>Orders over £0</span>
            </div>
            <div className='subtitle'>
              <span>£0 Orders</span>
              <Tooltip title={zeroOrderTooltipMessage}>
                <QuestionCircleOutlined className='_ml-8' />
              </Tooltip>
            </div>
          </PricingHeader>
          <PricingHeader>
            <div className='title'>
              <span>Pre-Order</span>
            </div>
            <div className='subtitle'>
              <span>Orders over £0</span>
            </div>
            <div className='subtitle'>
              <span>£0 Orders</span>
              <Tooltip title={zeroOrderTooltipMessage}>
                <QuestionCircleOutlined className='_ml-8' />
              </Tooltip>
            </div>
          </PricingHeader>
        </Col>
        <Col span={11}>
          <PricingTable>
            <div className='header'>
              <div className='title'>
                <span>Delivery</span>
              </div>
              <div className='subtitle'>
                <div>
                  <span>Slerp Courier</span>
                </div>
                <div>
                  <span>Own Drivers</span>
                </div>
                <div>
                  <span>Nationwide</span>
                </div>
              </div>
            </div>
            <div className='cell'>
              {getComissionRow([
                commission_rates.delivery,
                commission_rates.same_day_self_delivery,
                null
              ])}
              {getComissionRow([
                zero_commission_rates.delivery,
                zero_commission_rates.same_day_self_delivery,
                null
              ])}
            </div>
            <div className='cell'>
              {getComissionRow([
                commission_rates.preorder_delivery,
                commission_rates.preorder_self_delivery,
                commission_rates.nationwide_delivery
              ])}
              {getComissionRow([
                zero_commission_rates.preorder_delivery,
                zero_commission_rates.preorder_self_delivery,
                zero_commission_rates.nationwide_delivery
              ])}
            </div>
          </PricingTable>
        </Col>

        <Col span={5}>
          <PricingTable>
            <div className='header'>
              <div className='title'>
                <span>Click & Collect</span>
              </div>
            </div>
            <div className='cell'>
              {getComissionRow([commission_rates.pickup])}
              {getComissionRow([zero_commission_rates.pickup])}
            </div>
            <div className='cell'>
              {getComissionRow([commission_rates.preorder_pickup])}
              {getComissionRow([zero_commission_rates.preorder_pickup])}
            </div>
          </PricingTable>
        </Col>

        <Col span={5}>
          <PricingTable>
            <div className='header'>
              <div className='title'>
                <span>Table Ordering</span>
              </div>
            </div>
            <div className='cell'>
              {getComissionRow([commission_rates.table])}
              {getComissionRow([zero_commission_rates.table])}
            </div>
            <div className='cell'>
              {getComissionRow([null])}
              {getComissionRow([null])}
            </div>
          </PricingTable>
        </Col>
      </Row>

      {transactionFee && (
        <>
          <Row>
            <Col span={12}>
              <Title>
                <span>Transaction Fees</span>
              </Title>
            </Col>
          </Row>

          <Row align='middle'>
            <Col span={3}>
              <PricingHeader>
                <div className='title'>
                  <span>CARD TYPE</span>
                </div>
                <div className='subtitle'>
                  <span>Standards Card</span>
                </div>
                <div className='subtitle'>
                  <span>AMEX Cards</span>
                </div>
              </PricingHeader>
            </Col>
            <Col span={5}>
              <PricingTable>
                <div className='header'>
                  <div className='title'>
                    <span>UK</span>
                  </div>
                </div>
                <div className='cell'>
                  {getTableRow([
                    `${percentageToFixed(
                      transactionFee.percentage
                    )} + ${moneyToFixed(transactionFee.fixed_fee)}`
                  ])}
                  {getTableRow([
                    `${percentageToFixed(
                      transactionFee.amex_percentage
                    )} + ${moneyToFixed(transactionFee.fixed_fee)}`
                  ])}
                </div>
              </PricingTable>
            </Col>
            <Col span={1}></Col>
            <Col span={5}>
              <PricingTable>
                <div className='header'>
                  <div className='title'>
                    <span>International</span>
                  </div>
                </div>
                <div className='cell'>
                  {getTableRow([
                    <span>
                      <b style={{ color: '#d092dd' }}>UK</b> +{' '}
                      {percentageToFixed(
                        transactionFee.international_percentage
                      )}
                    </span>
                  ])}
                  {getTableRow([
                    <span>
                      <b style={{ color: '#d092dd' }}>UK</b> +{' '}
                      {percentageToFixed(
                        transactionFee.international_percentage
                      )}
                    </span>
                  ])}
                </div>
              </PricingTable>
            </Col>
          </Row>
        </>
      )}

      <Row style={{ marginTop: '32px' }} justify='end' className='_text-right'>
        <Col span={12}>* all pricing displayed is exclusive of VAT</Col>
      </Row>
    </Root>
  )
}

const Root = styled.div`
  max-width: 1728px;
`

const Title = styled.div`
  margin-top: 32px;
  font-size: 14px;
  font-weight: 600;
  color: #7e7e8f;
`

const PricingHeader = styled.div`
  margin-top: 12px;

  &:first-of-type {
    margin-top: 52px;
  }

  > .title {
    color: #d092dd;
    text-transform: uppercase;
    font-weight: 700;
  }

  > .subtitle {
    display: flex;
    align-items: center;
    height: 30px;
    font-style: italic;

    > * {
      font-weight: 600;
    }
  }
`

const PricingTable = styled.div`
  border: 2px solid #e7e5e9;
  border-radius: 12px;
  margin: 0 18px;
  font-weight: 500;

  > .header {
    display: flex;
    flex-flow: column;
    justify-content: center;

    font-size: 14px;
    height: 60px;

    > .title {
      color: #d092dd;
      text-transform: uppercase;
      text-align: center;
      font-weight: 700;
    }

    > .subtitle {
      font-style: italic;
      display: flex;
      justify-content: space-around;

      > * {
        font-weight: 600;
      }
    }
  }

  > .cell {
    border-top: 2px solid #e7e5e9;
    padding: 18px 0 6px 0;

    > .row {
      height: 32px;
      margin: 0;

      &:nth-child(2n) {
        border-top: 1px solid #e7e5e9;
      }
    }
  }
`

export default CommissionRates
